import React, { useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, TextField,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import axiosInstance from '../../axiosInstance';
import { themeExpert } from '../../theme/expert';
import ChampDate from '../ChampDate/ChampDate';

export const STATUTS = [
  { code: 'notstart', libelle: 'Pas commencée', couleur: 'default' },
  { code: 'start', libelle: 'Commencée', couleur: 'primary' },
  { code: 'complete', libelle: 'Terminée', couleur: 'success' },
  { code: 'delete', libelle: 'Annulée', couleur: 'error' },
];

const OUTILS_QUILL = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ['link', 'image'],
    ['clean'],
  ],
};

const FORMATS_QUILL = [
  'header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet',
  'indent', 'color', 'background', 'align', 'link', 'image',
];

/** Un champ email qui propose les intervenants du dossier. */
function ChampEmail({ label, valeur, onChange, contacts, aide }) {
  return (
    <Autocomplete
      freeSolo
      options={contacts}
      getOptionLabel={(o) => (typeof o === 'string' ? o : (o.mail || ''))}
      renderOption={(props, contact) => (
        <li {...props} key={contact.id}>
          <Box>
            <Typography variant="body2">{contact.mail}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[contact.firstname, contact.name,
                contact.role1 && `(${contact.role1})`].filter(Boolean).join(' ')}
            </Typography>
          </Box>
        </li>
      )}
      inputValue={valeur || ''}
      onInputChange={(_, v) => onChange(v)}
      renderInput={(params) => (
        <TextField {...params} label={label} helperText={aide} />
      )}
    />
  );
}

/**
 * La fenêtre d'une tâche — normale, ou courriel à envoyer.
 *
 * `hierarchy` porte à la fois un texte libre et les marqueurs EMAIL / XMAIL :
 * c'est ainsi que le dossier distingue une tâche d'un envoi, et le reste de
 * l'application s'appuie dessus. On le lit et l'écrit tel quel.
 */
/** Un groupe de pièces à joindre, avec « tout cocher ». */
function GroupePieces({ titre, pieces, photos, choisies, onBasculer, onChoisir }) {
  if (pieces.length === 0) return null;
  const ids = pieces.map((p) => p.id);
  const nombre = ids.filter((id) => choisies.includes(id)).length;
  return (
    <Box sx={{ mb: 1 }}>
      <FormControlLabel
        control={(
          <Checkbox size="small" checked={nombre === ids.length}
                    indeterminate={nombre > 0 && nombre < ids.length}
                    onChange={(e) => onChoisir(ids, e.target.checked)} />
        )}
        label={<Typography variant="subtitle2">{titre} ({pieces.length})</Typography>}
      />
      <Box sx={photos
        ? { display: 'grid', gap: 1, pl: 3,
            gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))' }
        : { pl: 3 }}>
        {pieces.map((piece) => (photos ? (
          <Box key={piece.id} onClick={() => onBasculer(piece.id)}
               title={piece.title || ''}
               sx={{ position: 'relative', cursor: 'pointer', borderRadius: 1,
                     overflow: 'hidden', border: '2px solid',
                     borderColor: choisies.includes(piece.id)
                       ? 'primary.main' : 'transparent' }}>
            <Box component="img" src={piece.thumbnail || piece.file} alt=""
                 loading="lazy"
                 sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover',
                       display: 'block' }} />
            <Checkbox size="small" checked={choisies.includes(piece.id)}
                      sx={{ position: 'absolute', top: 0, left: 0, p: 0.25,
                            bgcolor: 'background.paper', borderRadius: 0.5 }} />
          </Box>
        ) : (
          <FormControlLabel
            key={piece.id}
            sx={{ display: 'flex' }}
            control={(
              <Checkbox size="small" checked={choisies.includes(piece.id)}
                        onChange={() => onBasculer(piece.id)} />
            )}
            label={(
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">
                  {piece.title || piece.fichier}
                </Typography>
                {piece.category && (
                  <Chip size="small" variant="outlined" label={piece.category} />
                )}
              </Stack>
            )}
          />
        )))}
      </Box>
    </Box>
  );
}

export default function TaskDialog({
  ouvert, onFermer, tache, setTache, onEnregistrer, onEnvoyer,
  modeles, contacts, pieces, piecesJointes, onBasculerPiece, onChoisirPieces,
  parLien, setParLien, edition, occupe, envoi,
  liaisons = [], liaisonChoisie, setLiaisonChoisie,
}) {
  const [corrige, setCorrige] = useState(false);
  const [erreur, setErreur] = useState('');

  if (!tache) return null;

  const estCourriel = tache.hierarchy === 'EMAIL' || tache.hierarchy === 'XMAIL';
  // Un partage part vers un dossier relié, par la plateforme, sans courriel.
  const estPartage = tache.hierarchy === 'PARTAGE' || tache.hierarchy === 'XPARTAGE';
  const dejaEnvoye = ['XMAIL', 'XPARTAGE', 'XRECU'].includes(tache.hierarchy);
  const mode = (estCourriel && 'courriel') || (estPartage && 'partage') || 'tache';

  const poser = (champ) => (valeur) =>
    setTache((etat) => ({ ...etat, [champ]: valeur }));

  /**
   * Passer d'une tâche à un courriel, et l'inverse.
   *
   * Le destinataire d'une tâche est celui qui doit la traiter — donc celui
   * qui enverra le courriel. En basculant, il devient l'expéditeur, et le
   * destinataire reste à désigner. Au retour, le chemin inverse.
   */
  const basculer = (vers) => setTache((etat) => {
    const depuisTache = !['EMAIL', 'PARTAGE'].includes(etat.hierarchy);
    if (vers === 'tache') {
      return { ...etat, hierarchy: '', receiver: etat.sender || '', sender: '' };
    }
    return {
      ...etat,
      ...(depuisTache ? { sender: etat.receiver || '', receiver: '' } : {}),
      ...(vers === 'partage' ? { receiver: '' } : {}),
      hierarchy: vers === 'courriel' ? 'EMAIL' : 'PARTAGE',
    };
  });

  const appliquerModele = async (id) => {
    if (!id) return;
    try {
      const { data } = await axiosInstance.get(`/templates/${id}/`,
                                               { params: { expertise_id: tache.expertise } });
      setTache((etat) => ({ ...etat, comment: data.content }));
    } catch {
      setErreur("Le modèle n'a pas pu être chargé.");
    }
  };

  const corriger = async () => {
    setCorrige(true);
    try {
      const { data } = await axiosInstance.post(`/tasks/${tache.id}/correct/`,
                                                { text: tache.comment });
      setTache((etat) => ({ ...etat,
                            comment: data.correction || etat.comment }));
    } catch {
      setErreur("La correction n'a pas abouti.");
    } finally {
      setCorrige(false);
    }
  };

  const blocPieces = (
    <>
                {pieces.length > 0 && (
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center"
                           sx={{ mb: 1 }}>
                      <Typography variant="body2">Pièces jointes</Typography>
                      {piecesJointes.length > 0 && (
                        <Chip size="small" color="primary"
                              label={piecesJointes.length} />
                      )}
                    </Stack>
                    <Box sx={{ maxHeight: 260, overflowY: 'auto', p: 1,
                               border: '1px solid', borderColor: 'divider',
                               borderRadius: 1 }}>
                      <GroupePieces titre="Documents"
                                    pieces={pieces.filter((p) => p.fichier)}
                                    choisies={piecesJointes}
                                    onBasculer={onBasculerPiece}
                                    onChoisir={onChoisirPieces} />
                      <GroupePieces titre="Photos" photos
                                    pieces={pieces.filter((p) => !p.fichier)}
                                    choisies={piecesJointes}
                                    onBasculer={onBasculerPiece}
                                    onChoisir={onChoisirPieces} />
                    </Box>
                    {estCourriel && (<>
                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={<Checkbox size="small" checked={parLien}
                                         onChange={(e) => setParLien(e.target.checked)} />}
                      label={<Typography variant="body2">
                        Envoyer un lien de téléchargement plutôt que des pièces jointes
                      </Typography>} />
                    <Typography variant="caption" color="text.secondary"
                                component="div">
                      {parLien
                        ? "Le courriel ne porte qu'un lien, valable 30 jours. Les pièces partent en qualité d'origine, et vous verrez quand elles ont été téléchargées."
                        : "Les photos partent allégées. Au-delà de 15 Mo, l'envoi est réparti en plusieurs courriels ; une pièce trop lourde part par lien de téléchargement."}
                    </Typography>
                    </>)}
                    {estPartage && (
                      <Typography variant="caption" color="text.secondary"
                                  component="div" sx={{ mt: 1 }}>
                        Les pièces partent en qualité d'origine. L'autre partie
                        les reçoit dans une zone « à reprendre » et choisit ce
                        qu'elle garde.
                      </Typography>
                    )}
                  </Box>
                )}
    </>
  );

  return (
    <ThemeProvider theme={themeExpert}>
      <Dialog open={ouvert} onClose={onFermer} fullWidth maxWidth="lg">
        <DialogTitle>
          {edition ? 'Modifier la tâche' : 'Nouvelle tâche'}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2.5}>
            {erreur && <Alert severity="error"
                              onClose={() => setErreur('')}>{erreur}</Alert>}

            <Stack direction="row" spacing={2} alignItems="center"
                   flexWrap="wrap" useFlexGap>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={mode}
                onChange={(_, valeur) => {
                  if (!valeur || dejaEnvoye) return;
                  basculer(valeur);
                }}
              >
                <ToggleButton value="tache" disabled={dejaEnvoye}>
                  Tâche
                </ToggleButton>
                <ToggleButton value="courriel" disabled={dejaEnvoye}>
                  Courriel
                </ToggleButton>
                <ToggleButton value="partage" disabled={dejaEnvoye}>
                  Partage
                </ToggleButton>
              </ToggleButtonGroup>
              {tache.hierarchy === 'XMAIL' && (
                <Chip size="small" color="success" label="Courriel déjà envoyé" />
              )}
              {tache.hierarchy === 'XPARTAGE' && (
                <Chip size="small" color="success" label="Partage déjà envoyé" />
              )}
              {tache.hierarchy === 'XRECU' && (
                <Chip size="small" color="info" label="Reçu d'un dossier relié" />
              )}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Titre" value={tache.title || ''}
                         onChange={(e) => poser('title')(e.target.value)}
                         required />
              <TextField select label="Statut" value={tache.status || 'notstart'}
                         onChange={(e) => poser('status')(e.target.value)}
                         sx={{ minWidth: 200 }}>
                {STATUTS.map(({ code, libelle }) => (
                  <MenuItem key={code} value={code}>{libelle}</MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
                   alignItems={{ sm: 'center' }}>
              <ChampDate label="Échéance"
                         value={tache.review_at || ''}
                         onChange={(e) => poser('review_at')(e.target.value)}
                         InputLabelProps={{ shrink: true }}
                         sx={{ maxWidth: { sm: 220 } }} />
              <FormControlLabel
                control={<Checkbox checked={Boolean(tache.public)}
                                   onChange={(e) => poser('public')(e.target.checked)} />}
                label="Visible par ceux avec qui le dossier est partagé"
              />
            </Stack>

            {mode === 'tache' && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <ChampEmail label="Assignée à" valeur={tache.receiver}
                            onChange={poser('receiver')} contacts={contacts}
                            aide="Un intervenant du dossier, ou une autre adresse." />
                <TextField label="Hiérarchie" value={tache.hierarchy || ''}
                           onChange={(e) => poser('hierarchy')(e.target.value)}
                           helperText=" " />
              </Stack>
            )}

            {estCourriel && (
              <>
                <ChampEmail label="Expéditeur" valeur={tache.sender}
                            onChange={poser('sender')} contacts={contacts} />
                <ChampEmail label="Destinataire" valeur={tache.receiver}
                            onChange={poser('receiver')} contacts={contacts}
                            aide={contacts.length > 0
                              ? `${contacts.length} intervenant${contacts.length > 1 ? 's' : ''} du dossier ont une adresse.`
                              : "Aucun intervenant du dossier n'a d'adresse."} />
                <ChampEmail label="Copie à" valeur={tache.copy_receiver}
                            onChange={poser('copy_receiver')} contacts={contacts}
                            aide="Plusieurs adresses séparées par des virgules." />
                <TextField label="Objet" value={tache.title_bis || ''}
                           onChange={(e) => poser('title_bis')(e.target.value)} />

                {blocPieces}
              </>
            )}

            {estPartage && (
              <>
                {liaisons.length === 0 && tache.hierarchy === 'PARTAGE' ? (
                  <Alert severity="info">
                    Aucun dossier n'est relié à celui-ci. Dans Intervenants,
                    choisissez « Relier à son dossier » pour inviter une partie.
                  </Alert>
                ) : tache.hierarchy === 'PARTAGE' && (
                  <TextField select label="Envoyer au dossier de"
                             value={liaisonChoisie || ''}
                             onChange={(e) => setLiaisonChoisie(e.target.value)}
                             helperText="Une tâche apparaîtra dans son dossier, visible par son organisation.">
                    {liaisons.map((l) => (
                      <MenuItem key={l.id} value={l.id}>
                        {[l.intervenant_libelle, l.organisation].filter(Boolean).join(' — ')}
                        {l.reference ? ` — dossier ${l.reference}` : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {tache.hierarchy === 'XPARTAGE' && (
                  <TextField label="Envoyé à" value={tache.receiver || ''} disabled />
                )}
                <TextField label="Objet" value={tache.title_bis || ''}
                           onChange={(e) => poser('title_bis')(e.target.value)} />
                {tache.hierarchy === 'PARTAGE' && blocPieces}
              </>
            )}

            <TextField select label="Partir d'un modèle" value=""
                       onChange={(e) => appliquerModele(e.target.value)}
                       helperText="Son contenu remplacera le corps du message.">
              <MenuItem value=""><em>Aucun</em></MenuItem>
              {modeles.map((modele) => (
                <MenuItem key={modele.id} value={modele.id}>
                  {modele.title}
                </MenuItem>
              ))}
            </TextField>

            <Box>
              <Stack direction="row" spacing={2} alignItems="center"
                     justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  {estCourriel ? 'Corps du message' : 'Commentaire'}
                </Typography>
                {edition && (
                  <Button size="small" variant="outlined" onClick={corriger}
                          disabled={corrige || !tache.comment}>
                    {corrige ? 'Correction…' : "Corriger le texte par l'IA"}
                  </Button>
                )}
              </Stack>
              {/* Quill pose sa propre hauteur : on la contraint ici plutôt
                  que par une feuille de style globale. */}
              <Box sx={{ '& .ql-container': { minHeight: 280 },
                         '& .ql-editor': { minHeight: 280 } }}>
                <ReactQuill
                  theme="snow"
                  value={tache.comment || ''}
                  onChange={poser('comment')}
                  modules={OUTILS_QUILL}
                  formats={FORMATS_QUILL}
                  placeholder={estCourriel ? 'Votre message…' : 'Vos notes…'}
                />
              </Box>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onFermer} disabled={occupe || envoi}>
            Fermer
          </Button>
          <Button variant="contained" onClick={() => onEnregistrer(false)}
                  disabled={occupe || envoi || !tache.title?.trim()}>
            {occupe ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
          {tache.hierarchy === 'PARTAGE' && (
            <Button variant="contained" color="success" onClick={onEnvoyer}
                    disabled={occupe || envoi || !liaisonChoisie
                              || !tache.title?.trim()}>
              {envoi ? 'Envoi…' : `Envoyer au dossier relié${piecesJointes.length > 0
                ? ` (${piecesJointes.length} pièce${piecesJointes.length > 1 ? 's' : ''})` : ''}`}
            </Button>
          )}
          {estCourriel && (
            <Button variant="contained" color="success" onClick={onEnvoyer}
                    disabled={occupe || envoi || dejaEnvoye
                              || !tache.title?.trim()}>
              {envoi ? 'Envoi…' : `Envoyer${piecesJointes.length > 0
                ? ` (${piecesJointes.length} ${parLien ? 'par lien' : 'PJ'})` : ''}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
